import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { logError } from '@/lib/logger';
import GoogleSignInButton from '@/components/GoogleSignInButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SignUpPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    preferred_name: '',
    referral_code: searchParams.get('ref') || '',
  });

  // Validate referral code if present in URL
  useEffect(() => {
    const validateReferral = async () => {
      const code = searchParams.get('ref');
      if (code) {
        try {
          const response = await axios.post(`${API}/referral/validate`, { code });
          if (response.data.valid) {
            setReferralInfo(response.data);
            setFormData(prev => ({ ...prev, referral_code: code.toUpperCase() }));
          }
        } catch (error) {
          // Invalid referral code - continue without bonus (non-critical)
                      logError('Referral validation skipped:', error?.response?.status);
        }
      }
    };
    validateReferral();
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signup(
        formData.email,
        formData.password,
        formData.name,
        formData.preferred_name,
        formData.referral_code || null
      );
      toast.success('Account created successfully!');
      navigate('/consent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1
            className="text-4xl md:text-5xl font-medium tracking-tight text-stone-800 mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="signup-heading"
          >
            Welcome
          </h1>
          <p className="text-stone-600">This is just for you. No judgment. No rush.</p>
        </div>

        {/* Referral Bonus Banner */}
        {referralInfo && (
          <div 
            className="mb-6 p-4 rounded-2xl text-center"
            style={{ 
              background: 'linear-gradient(135deg, rgba(232,180,184,0.2) 0%, rgba(201,168,76,0.15) 100%)',
              border: '1px solid rgba(232,180,184,0.3)'
            }}
          >
            <p className="text-stone-700 font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
              {referralInfo.referrer_name} is giving you {referralInfo.total_free_days} days free!
            </p>
            <p className="text-sm text-stone-500 mt-1">
              No credit card required for your extended trial
            </p>
          </div>
        )}

        <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8">
          <div className="space-y-4 mb-6">
            <GoogleSignInButton dataTestId="signup-google-button" label="Sign up with Google" />
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-stone-200" />
              <span className="text-xs uppercase tracking-wider text-stone-400">or with email</span>
              <div className="h-px flex-1 bg-stone-200" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="signup-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-white/60 border-stone-200 focus:border-stone-400 focus:ring-4 focus:ring-stone-100 rounded-xl"
                data-testid="signup-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a secure password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-white/60 border-stone-200 focus:border-stone-400 focus:ring-4 focus:ring-stone-100 rounded-xl"
                data-testid="signup-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="preferred_name">What should Sage call you?</Label>
              <Input
                id="preferred_name"
                type="text"
                placeholder="Your name or whatever feels right"
                value={formData.preferred_name}
                onChange={(e) =>
                  setFormData({ ...formData, preferred_name: e.target.value })
                }
                className="bg-white/60 border-stone-200 focus:border-stone-400 focus:ring-4 focus:ring-stone-100 rounded-xl"
                data-testid="signup-preferred-name-input"
              />
            </div>

            {/* Referral Code Field */}
            <div className="space-y-2">
              <Label htmlFor="referral_code">Referral Code (Optional)</Label>
              <Input
                id="referral_code"
                type="text"
                placeholder="HEAL-XXXX"
                value={formData.referral_code}
                onChange={(e) =>
                  setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })
                }
                className="bg-white/60 border-stone-200 focus:border-stone-400 focus:ring-4 focus:ring-stone-100 rounded-xl font-mono"
                data-testid="signup-referral-code-input"
              />
              {!referralInfo && formData.referral_code && (
                <p className="text-xs text-stone-500">
                  Have a friend's code? Get 21 days free instead of 14!
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full px-8 py-6 text-lg bg-stone-800 text-stone-50 hover:bg-stone-700 shadow-lg transition-all duration-300 hover:scale-[1.02]"
              data-testid="signup-submit-button"
            >
              {(() => {
                if (loading) return 'Creating Account...';
                if (referralInfo) return 'Start My 21-Day Trial';
                return 'Begin my healing';
              })()}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-stone-600">
            Already have an account?{' '}
            <Link to="/login" className="text-stone-800 font-medium hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
